/** Strings of the analysis tooling (history list, chat transcripts, mailbox, e-mail routing, voice meetings, re-analysis, repo pairing…): `analysisToolsEn` is the source, `analysisToolsCs` must have the same keys. */
export const analysisToolsEn = {
  analysisTools: {
    historyList: {
      sources: {
        meeting: "Meeting transcript",
        whatsapp: "WhatsApp",
        email: "Email",
        discord: "Discord",
      },
      filters: {
        all: "All",
        email: "Email",
        whatsapp: "WhatsApp",
        discord: "Discord",
        meeting: "Meeting",
      },
      loadFailed: "Couldn't load saved analyses.",
      emptyEmail: "No analysed emails yet. Forward a client email to the watched mailbox.",
      empty: "Nothing here yet.",
      applied: "{{approved}}/{{total}} applied",
      noSummary: "(no summary)",
    },
    chatPage: {
      loadFailed_chat: "Couldn't load this chat.",
      loadFailed_channel: "Couldn't load this channel.",
      default_chat: "Chat",
      default_channel: "Channel",
      subtitle: "Full transcript, with what each analysis run created shown inline.",
      back: "Back",
    },
    chat: {
      empty: "No messages stored for this chat yet.",
      contextOnly: "· context only",
      contextOnlyTitle: "Before the analysis date — read as context, never proposed from",
      notAnalysed: "· not analysed yet",
      ledToSuggestion: "✦ led to a suggestion",
      ledToSuggestionTitle: "An AI suggestion was proposed here — open the analysis",
      enlarge: "Enlarge image",
      attachment: "Chat attachment",
      closeImage: "Close image",
      attachmentEnlarged: "Chat attachment, enlarged",
      noActions: "Analysed — no actions were applied.",
      viewAnalysis: "view analysis",
      open: "open",
    },
    emailRouting: {
      loadFailed: "Couldn't load email routing.",
      needBoth: "Enter a sender/domain and pick a project.",
      addFailed: "Couldn't add this mapping (maybe the sender/domain is already mapped).",
      intro:
        "Map a client's email address (<code>jan@klient.cz</code>) or whole domain (<code>klient.cz</code>) to a project. Forwarded emails involving a matched address are analysed with that project's open work items as context.",
      patternPlaceholder: "sender@klient.cz or klient.cz",
      pickProject: "— pick a project —",
      add: "Add",
      none: "No mappings yet.",
      remove: "Remove",
    },
    historyFrom: {
      label: "Suggest from",
      help: "Suggestions come only from messages sent on or after this date. Hub also loads the month before it, but only as context the AI reads to understand them — nothing older is ever proposed. Leave it empty to start from now.",
      context: " (+{{count}} for context)",
      suggestsFrom: "suggests from {{date}}{{context}}",
      noHistory: "no history loaded{{context}}",
    },
    mailbox: {
      analysed: "Analysed — see the Suggestions queue",
      analyseFailed: "Couldn't analyse this email.",
      off: "Mailbox isn't configured. Set <code>IMAP_PASS</code> on the server to read the inbox.",
      none: "No recent messages.",
      viewAnalysis: "view analysis",
      analysing: "Analysing…",
      run: "Run analysis",
    },
    meetingProject: {
      saveFailed: "Couldn't change the meeting's project",
      label: "Meeting project",
      none: "— no project —",
    },
    reanalyse: {
      errors: {
        noAi: "AI is not configured on the server. Set up the LiteLLM gateway and try again.",
        gone: "This analysis no longer exists.",
        failedDetail: "Couldn't analyse: {{detail}}",
        failed: "Couldn't run the analysis again.",
        stream: "The AI analysis failed: {{detail}}",
        unknown: "unknown error",
        lost: "Lost the connection while the analysis was running. It may have finished anyway — reload this page and check “Re-run” before running it again.",
        timeout: "Couldn't run the analysis again (it timed out).",
        noResponse: "Couldn't run the analysis again (no response from the server).",
      },
      created_one: "New analysis with {{count}} proposal",
      created_other: "New analysis with {{count}} proposals",
      created_few: "New analysis with {{count}} proposals",
      open: "Analyse again",
      title: "Analyse this conversation again",
      intro:
        "Runs the AI over the same stored conversation once more. Add a note on what it should do better — a task it missed, something it invented, a project it got wrong. This analysis and its proposals stay as they are; the re-run is saved as a new one.",
      noteLabel: "What should it do better?",
      notePlaceholder:
        "e.g. You missed the agreed task to set up the staging server for Acme — capture every commitment, including the ones stated in passing.",
      phase: { thinking: "Thinking", writing: "Writing proposals" },
      progress: "{{phase}}… {{tokens}} tokens",
      running: "Analysing…",
    },
    tracker: {
      title: "Hub tracker",
      publishing: "Hub turns your tracker's evidence into proposals here, once a day is over.",
      shadow:
        "Shadow mode: Hub builds proposals from your tracker's evidence but keeps them out of this queue.",
      lastEvidence: "Last evidence",
      toShadow: "Back to shadow mode",
      toPublish: "Put Hub's proposals here",
    },
    transcript: {
      errors: {
        noAi: "AI is not configured on the server. Set up the LiteLLM gateway and try again.",
        empty: "There was nothing to analyse in this transcript.",
        failedDetail: "Couldn't analyse: {{detail}}",
        failed: "Couldn't analyse the transcript.",
        stream: "The AI analysis failed: {{detail}}",
        unknown: "unknown error",
        lost: "Lost the connection while the analysis was running. It may have finished anyway — check the Meetings list before analysing it again.",
        timeout: "Couldn't analyse the transcript (it timed out).",
        noResponse: "Couldn't analyse the transcript (no response from the server).",
      },
      pasteFirst: "Paste a meeting transcript first.",
      open: "+ Analyse a meeting transcript",
      title: "Analyse a meeting transcript",
      projects: "Projects discussed",
      projectsPlaceholder: "All / no specific project",
      projectsNoun: "projects",
      projectsHelp:
        "Pick the project(s) the meeting concerned so the AI can suggest changes to their open work items. Leave empty to only get new-item / idea suggestions.",
      text: "Transcript text",
      placeholder: "Paste the meeting transcript here…",
      added_one: "Added {{count}} suggestion to the queue below.",
      added_other: "Added {{count}} suggestions to the queue below.",
      added_few: "Added {{count}} suggestions to the queue below.",
      running: "Analysing…",
      run: "Analyse transcript",
      progressLabel: "Analysing the transcript",
      reading: "Reading the transcript…",
      writing: "Writing suggestions…",
      tokens: "{{value}} tokens",
    },
    repo: {
      moved_one: "{{count}} waiting proposal moved to it",
      moved_other: "{{count}} waiting proposals moved to it",
      moved_few: "{{count}} waiting proposals moved to it",
      nowFeeds: "now feeds {{name}}",
      alreadyOf: "already {{name}}'s",
      paired: "Paired — {{how}}, {{moved}}.",
      pairFailed: "Couldn't pair this repository.",
      dialogTitle: "Pair repository with a project",
      dialogDescription:
        "The time tracker resolved this time to a repository Hub has no project for, so nobody can file it yet. Pairing attaches the repository to a project and moves every waiting proposal from it at once — the project keeps its own repository.",
      repository: "Repository",
      gitlabId: "GitLab id",
      checkout: "Checkout",
      waiting: "Waiting",
      alreadyBound:
        "Hub already binds this repository to <strong>{{name}}</strong>. Pairing here only hands the waiting proposals over to it.",
      hubProject: "Hub project",
      pickProject: "— pick a project —",
      createHint:
        "This repository will feed the picked project its time. Not in Hub yet? <create>Create the project</create> — its waiting time follows it automatically.",
      pairing: "Pairing…",
      pair: "Pair project",
      withoutProject: "Repositories without a project",
      withoutProjectHelp:
        "The tracker knows which repository this time came from, but Hub has no project bound to it — so these proposals can't be filed. Pair the repository and every proposal behind it gets its project.",
      waitingProposals_one: "{{count}} waiting proposal",
      waitingProposals_other: "{{count}} waiting proposals",
      waitingProposals_few: "{{count}} waiting proposals",
      adminOnly: "An admin has to pair this repository.",
    },
    voice: {
      status: {
        scheduled: "Scheduled",
        joining: "Connecting…",
        recording: "Recording…",
        processing: "Transcribing & analysing…",
        completed: "Completed",
        failed: "Failed",
        cancelled: "Cancelled",
      },
      botNotConfigured: "The Discord bot is not configured (DISCORD_BOT_TOKEN).",
      listFailed: "Couldn't list voice channels.",
      startFailed: "Couldn't start the recording.",
      alreadyOnCall: "Hubert is already on this call — no second bot was sent.",
      meetFailed: "Couldn't send the Meet bot.",
      title: "Voice meetings",
      intro:
        "Invite Hubert into a Discord voice channel or a Google Meet call — he records, transcribes, and proposes follow-ups. For Meet, adding hub@netlion.cz to the calendar invite schedules him automatically.",
      meetOpen: "Meet meeting",
      meetTitle: "Send Hubert to a Google Meet",
      meetMissing:
        "The Meet bot is not configured — set <code>ATTENDEE_URL</code> and <code>ATTENDEE_API_KEY</code> on the server first.",
      meetLink: "Google Meet link",
      joinAt: "Join at (empty = now)",
      project: "Project (analysis context)",
      noProject: "— no project —",
      sending: "Sending…",
      scheduleBot: "Schedule bot",
      sendNow: "Send bot now",
      recordOpen: "Record meeting",
      recordTitle: "Record a Discord meeting",
      loadingChannels: "Loading channels…",
      sttMissing:
        "Speech-to-text is not configured — set <code>STT_API_KEY</code> on the server first.",
      channel: "Voice channel",
      joining: "Joining…",
      join: "Join & record",
      waitingForBot: "Waiting for Hubert to join the call",
      joinsAt: "joins",
      sentAt: "sent",
      startedAt: "started",
      noAudio: "no audio for {{minutes}} min",
      endAnalyse: "End & analyse",
      stopAnalyse: "Stop & analyse",
      cancel: "Cancel",
      utterances_one: "{{count}} utterance",
      utterances_other: "{{count}} utterances",
      utterances_few: "{{count}} utterances",
      incomplete: "Incomplete recording",
      viewAnalysis: "view analysis",
      transcribeAgain: "Transcribe again",
      noJoin: "Hubert didn't join",
      retry: "Retry",
      audio: "Meeting recording",
    },
  },
};
export const analysisToolsCs: typeof analysisToolsEn = {
  analysisTools: {
    historyList: {
      sources: {
        meeting: "Přepis schůzky",
        whatsapp: "WhatsApp",
        email: "E-mail",
        discord: "Discord",
      },
      filters: {
        all: "Vše",
        email: "E-mail",
        whatsapp: "WhatsApp",
        discord: "Discord",
        meeting: "Schůzka",
      },
      loadFailed: "Uložené analýzy se nepodařilo načíst.",
      emptyEmail:
        "Zatím žádné analyzované e-maily. Přepošlete e-mail klienta na sledovanou schránku.",
      empty: "Zatím tu nic není.",
      applied: "použito {{approved}}/{{total}}",
      noSummary: "(bez shrnutí)",
    },
    chatPage: {
      loadFailed_chat: "Tuto konverzaci se nepodařilo načíst.",
      loadFailed_channel: "Tento kanál se nepodařilo načíst.",
      default_chat: "Konverzace",
      default_channel: "Kanál",
      subtitle: "Celý přepis, přímo v něm je vidět, co každé spuštění analýzy vytvořilo.",
      back: "Zpět",
    },
    chat: {
      empty: "Pro tuto konverzaci zatím nejsou uložené žádné zprávy.",
      contextOnly: "· jen kontext",
      contextOnlyTitle: "Před datem analýzy — čte se jako kontext, nikdy se z něj nic nenavrhuje",
      notAnalysed: "· zatím neanalyzováno",
      ledToSuggestion: "✦ vedlo k návrhu",
      ledToSuggestionTitle: "Zde vznikl návrh od AI — otevřít analýzu",
      enlarge: "Zvětšit obrázek",
      attachment: "Příloha konverzace",
      closeImage: "Zavřít obrázek",
      attachmentEnlarged: "Příloha konverzace, zvětšená",
      noActions: "Analyzováno — nebyla použita žádná akce.",
      viewAnalysis: "zobrazit analýzu",
      open: "otevřít",
    },
    emailRouting: {
      loadFailed: "Směrování e-mailů se nepodařilo načíst.",
      needBoth: "Zadejte odesílatele/doménu a vyberte projekt.",
      addFailed: "Toto přiřazení se nepodařilo přidat (možná už je odesílatel/doména přiřazená).",
      intro:
        "Přiřaďte e-mailovou adresu klienta (<code>jan@klient.cz</code>) nebo celou doménu (<code>klient.cz</code>) k projektu. Přeposlané e-maily s odpovídající adresou se analyzují s otevřenými položkami práce toho projektu jako kontextem.",
      patternPlaceholder: "odesilatel@klient.cz nebo klient.cz",
      pickProject: "— vyberte projekt —",
      add: "Přidat",
      none: "Zatím žádná přiřazení.",
      remove: "Odebrat",
    },
    historyFrom: {
      label: "Navrhovat od",
      help: "Návrhy vznikají jen ze zpráv odeslaných v tento den nebo později. Hub načte i měsíc před ním, ale jen jako kontext, ze kterého AI zprávám rozumí — nic staršího se nikdy nenavrhuje. Nechte prázdné, pokud chcete začít od teď.",
      context: " (+{{count}} jako kontext)",
      suggestsFrom: "navrhuje od {{date}}{{context}}",
      noHistory: "historie nenačtena{{context}}",
    },
    mailbox: {
      analysed: "Analyzováno — viz fronta návrhů",
      analyseFailed: "Tento e-mail se nepodařilo analyzovat.",
      off: "Schránka není nastavená. Pro čtení doručené pošty nastavte na serveru <code>IMAP_PASS</code>.",
      none: "Žádné nedávné zprávy.",
      viewAnalysis: "zobrazit analýzu",
      analysing: "Analyzuji…",
      run: "Spustit analýzu",
    },
    meetingProject: {
      saveFailed: "Projekt schůzky se nepodařilo změnit",
      label: "Projekt schůzky",
      none: "— bez projektu —",
    },
    reanalyse: {
      errors: {
        noAi: "AI není na serveru nastavená. Nastavte bránu LiteLLM a zkuste to znovu.",
        gone: "Tato analýza už neexistuje.",
        failedDetail: "Analýza se nepodařila: {{detail}}",
        failed: "Analýzu se nepodařilo spustit znovu.",
        stream: "Analýza AI selhala: {{detail}}",
        unknown: "neznámá chyba",
        lost: "Při běhu analýzy se přerušilo spojení. Mohla přesto doběhnout — obnovte tuto stránku a před dalším spuštěním zkontrolujte „Re-run“.",
        timeout: "Analýzu se nepodařilo spustit znovu (vypršel čas).",
        noResponse: "Analýzu se nepodařilo spustit znovu (server neodpověděl).",
      },
      created_one: "Nová analýza s {{count}} návrhem",
      created_other: "Nová analýza s návrhy: {{count}}",
      created_few: "Nová analýza se {{count}} návrhy",
      open: "Analyzovat znovu",
      title: "Analyzovat tuto konverzaci znovu",
      intro:
        "Pustí AI ještě jednou přes stejnou uloženou konverzaci. Přidejte poznámku, co má udělat lépe — úkol, který minula, něco, co si vymyslela, projekt, který zvolila špatně. Tato analýza a její návrhy zůstanou beze změny; nové spuštění se uloží jako další.",
      noteLabel: "Co má udělat lépe?",
      notePlaceholder:
        "např. Minula jsi domluvený úkol nastavit staging server pro Acme — zachyť každý závazek, i ten řečený mimochodem.",
      phase: { thinking: "Přemýšlím", writing: "Píšu návrhy" },
      progress: "{{phase}}… tokenů: {{tokens}}",
      running: "Analyzuji…",
    },
    tracker: {
      title: "Hub tracker",
      publishing: "Hub z podkladů vašeho trackeru po skončení dne vytvoří návrhy tady.",
      shadow:
        "Stínový režim: Hub z podkladů vašeho trackeru návrhy vytváří, ale nezařazuje je do této fronty.",
      lastEvidence: "Poslední podklady",
      toShadow: "Zpět do stínového režimu",
      toPublish: "Dávat návrhy Hubu sem",
    },
    transcript: {
      errors: {
        noAi: "AI není na serveru nastavená. Nastavte bránu LiteLLM a zkuste to znovu.",
        empty: "V tomto přepisu nebylo co analyzovat.",
        failedDetail: "Analýza se nepodařila: {{detail}}",
        failed: "Přepis se nepodařilo analyzovat.",
        stream: "Analýza AI selhala: {{detail}}",
        unknown: "neznámá chyba",
        lost: "Při běhu analýzy se přerušilo spojení. Mohla přesto doběhnout — před dalším spuštěním zkontrolujte seznam schůzek.",
        timeout: "Přepis se nepodařilo analyzovat (vypršel čas).",
        noResponse: "Přepis se nepodařilo analyzovat (server neodpověděl).",
      },
      pasteFirst: "Nejdřív vložte přepis schůzky.",
      open: "+ Analyzovat přepis schůzky",
      title: "Analyzovat přepis schůzky",
      projects: "Projednávané projekty",
      projectsPlaceholder: "Všechny / žádný konkrétní projekt",
      projectsNoun: "projektů",
      projectsHelp:
        "Vyberte projekty, kterých se schůzka týkala, aby AI mohla navrhovat změny jejich otevřených položek práce. Nechte prázdné, pokud chcete jen návrhy nových položek a nápadů.",
      text: "Text přepisu",
      placeholder: "Vložte sem přepis schůzky…",
      added_one: "Do fronty níže byl přidán {{count}} návrh.",
      added_other: "Do fronty níže byly přidány návrhy: {{count}}.",
      added_few: "Do fronty níže byly přidány {{count}} návrhy.",
      running: "Analyzuji…",
      run: "Analyzovat přepis",
      progressLabel: "Analýza přepisu",
      reading: "Čtu přepis…",
      writing: "Píšu návrhy…",
      tokens: "tokenů: {{value}}",
    },
    repo: {
      moved_one: "{{count}} čekající návrh se přesunul",
      moved_other: "čekajících návrhů přesunuto: {{count}}",
      moved_few: "{{count}} čekající návrhy se přesunuly",
      nowFeeds: "nově plní projekt {{name}}",
      alreadyOf: "už patří projektu {{name}}",
      paired: "Spárováno — {{how}}, {{moved}}.",
      pairFailed: "Tento repozitář se nepodařilo spárovat.",
      dialogTitle: "Spárovat repozitář s projektem",
      dialogDescription:
        "Tracker času přiřadil tento čas k repozitáři, pro který Hub nemá projekt, takže ho zatím nikdo nemůže zapsat. Spárování připojí repozitář k projektu a najednou přesune všechny čekající návrhy z něj — projekt si ponechá svůj vlastní repozitář.",
      repository: "Repozitář",
      gitlabId: "ID v GitLabu",
      checkout: "Pracovní kopie",
      waiting: "Čeká",
      alreadyBound:
        "Hub už tento repozitář váže k projektu <strong>{{name}}</strong>. Spárování zde jen předá čekající návrhy tomuto projektu.",
      hubProject: "Projekt v Hubu",
      pickProject: "— vyberte projekt —",
      createHint:
        "Tento repozitář bude vybranému projektu dodávat čas. Ještě není v Hubu? <create>Vytvořte projekt</create> — jeho čekající čas ho následuje automaticky.",
      pairing: "Páruji…",
      pair: "Spárovat s projektem",
      withoutProject: "Repozitáře bez projektu",
      withoutProjectHelp:
        "Tracker ví, z jakého repozitáře tento čas pochází, ale Hub k němu nemá navázaný žádný projekt — proto se tyto návrhy nedají zapsat. Spárujte repozitář a každý návrh za ním dostane svůj projekt.",
      waitingProposals_one: "{{count}} čekající návrh",
      waitingProposals_other: "čekajících návrhů: {{count}}",
      waitingProposals_few: "{{count}} čekající návrhy",
      adminOnly: "Tento repozitář musí spárovat administrátor.",
    },
    voice: {
      status: {
        scheduled: "Naplánováno",
        joining: "Připojování…",
        recording: "Nahrávání…",
        processing: "Přepisování a analýza…",
        completed: "Dokončeno",
        failed: "Selhalo",
        cancelled: "Zrušeno",
      },
      botNotConfigured: "Discord bot není nastavený (DISCORD_BOT_TOKEN).",
      listFailed: "Hlasové kanály se nepodařilo vypsat.",
      startFailed: "Nahrávání se nepodařilo spustit.",
      alreadyOnCall: "Hubert už na tomto hovoru je — druhý bot nebyl poslán.",
      meetFailed: "Bota do Meetu se nepodařilo poslat.",
      title: "Hlasové schůzky",
      intro:
        "Pozvěte Huberta do hlasového kanálu na Discordu nebo do hovoru Google Meet — nahraje, přepíše a navrhne navazující kroky. U Meetu ho naplánuje automaticky přidání hub@netlion.cz do pozvánky v kalendáři.",
      meetOpen: "Schůzka v Meetu",
      meetTitle: "Poslat Huberta do Google Meetu",
      meetMissing:
        "Bot pro Meet není nastavený — nejdřív na serveru nastavte <code>ATTENDEE_URL</code> a <code>ATTENDEE_API_KEY</code>.",
      meetLink: "Odkaz na Google Meet",
      joinAt: "Připojit se v (prázdné = hned)",
      project: "Projekt (kontext analýzy)",
      noProject: "— bez projektu —",
      sending: "Odesílám…",
      scheduleBot: "Naplánovat bota",
      sendNow: "Poslat bota hned",
      recordOpen: "Nahrát schůzku",
      recordTitle: "Nahrát schůzku na Discordu",
      loadingChannels: "Načítání kanálů…",
      sttMissing:
        "Přepis řeči není nastavený — nejdřív na serveru nastavte <code>STT_API_KEY</code>.",
      channel: "Hlasový kanál",
      joining: "Připojuji se…",
      join: "Připojit a nahrávat",
      waitingForBot: "Čeká se, až se Hubert připojí k hovoru",
      joinsAt: "připojí se",
      sentAt: "odesláno",
      startedAt: "začalo",
      noAudio: "žádný zvuk už {{minutes}} min",
      endAnalyse: "Ukončit a analyzovat",
      stopAnalyse: "Zastavit a analyzovat",
      cancel: "Zrušit",
      utterances_one: "{{count}} promluva",
      utterances_other: "promluv: {{count}}",
      utterances_few: "{{count}} promluvy",
      incomplete: "Neúplná nahrávka",
      viewAnalysis: "zobrazit analýzu",
      transcribeAgain: "Přepsat znovu",
      noJoin: "Hubert se nepřipojil",
      retry: "Zkusit znovu",
      audio: "Nahrávka schůzky",
    },
  },
};
